import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { FiPrinter, FiSearch, FiCalendar, FiEye, FiX, FiFileText, FiEdit2, FiPlus, FiTrash2, FiDownload, FiLock, FiUnlock } from 'react-icons/fi';

function Bills() {
  const { alert, confirm } = useModal();
  const [bills, setBills] = useState([]);
  const [totalBills, setTotalBills] = useState(0);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [historyFilterMode, setHistoryFilterMode] = useState('all'); // all | month | date
  const currentYear = new Date().getFullYear();
  const [selectedHistoryMonth, setSelectedHistoryMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [selectedHistoryYear, setSelectedHistoryYear] = useState(String(currentYear));
  const [selectedHistoryDate, setSelectedHistoryDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [selectedBill, setSelectedBill] = useState(null);
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('completed'); // 'completed' or 'history'
  const [showEditBillModal, setShowEditBillModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [editBillItems, setEditBillItems] = useState([]);
  const [menuItemsForEdit, setMenuItemsForEdit] = useState([]);
  const [editDiscountPercent, setEditDiscountPercent] = useState('0');
  const [editDiscountType, setEditDiscountType] = useState('percent'); // 'percent' or 'amount'
  const [editDiscountFlatAmount, setEditDiscountFlatAmount] = useState('0');
  const [savingBillEdit, setSavingBillEdit] = useState(false);
  const [generatingOrderId, setGeneratingOrderId] = useState(null);
  const [printingBillId, setPrintingBillId] = useState(null);
  const [regeneratingBillId, setRegeneratingBillId] = useState(null);
  const [showDeleteBillModal, setShowDeleteBillModal] = useState(false);
  const [deletingBill, setDeletingBill] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingBillId, setDeletingBillId] = useState(null);
  const [billPdfPreviewEnabled, setBillPdfPreviewEnabled] = useState(false);
  const [billPrintCopies, setBillPrintCopies] = useState(1);
  const [paymentMethodsForEdit, setPaymentMethodsForEdit] = useState(['cash', 'card', 'upi', 'wallet']);
  const [editPaymentMethod, setEditPaymentMethod] = useState('cash');
  const [isFullBillEditUnlocked, setIsFullBillEditUnlocked] = useState(false);
  const [billEditUnlockPassword, setBillEditUnlockPassword] = useState('');
  const [verifyingBillEditPassword, setVerifyingBillEditPassword] = useState(false);
  const [billNoteDraft, setBillNoteDraft] = useState('');
  const [savingBillNote, setSavingBillNote] = useState(false);
  const [showExportFormatModal, setShowExportFormatModal] = useState(false);

  useEffect(() => {
    fetchCompletedOrders();
    fetchBills();
    fetchBillPdfSettings();
  }, []);

  const fetchBillPdfSettings = async () => {
    try {
      const response = await api.get('/api/settings/restaurant');
      const settingValue = String(response.data?.download_bill_as_pdf || 'false').trim().toLowerCase();
      setBillPdfPreviewEnabled(settingValue === 'true');
      const parsedCopies = parseInt(String(response.data?.bill_print_copies || '1'), 10);
      const resolvedCopies = Number.isFinite(parsedCopies) && parsedCopies > 0 ? Math.min(5, parsedCopies) : 1;
      setBillPrintCopies(resolvedCopies);

      const parsedMethods = String(response.data?.payment_methods || 'cash,card,upi,wallet')
        .split(',')
        .map((method) => method.trim().toLowerCase())
        .filter(Boolean);
      setPaymentMethodsForEdit(parsedMethods.length > 0 ? parsedMethods : ['cash', 'card', 'upi', 'wallet']);
    } catch (error) {
      console.error('Failed to fetch bill PDF setting:', error);
      setBillPdfPreviewEnabled(false);
      setBillPrintCopies(1);
      setPaymentMethodsForEdit(['cash', 'card', 'upi', 'wallet']);
    }
  };

  const getGroupedBillItems = (items = []) => {
    const grouped = new Map();

    (items || []).forEach((item) => {
      const name = String(item?.menu_items?.name || 'Item').trim() || 'Item';
      const note = String(item?.special_instructions || '').trim();
      const quantity = parseInt(item?.quantity, 10) || 0;
      const storedSubtotal = parseFloat(item?.subtotal || 0) || 0;
      const fallbackUnitFromSubtotal = quantity > 0 ? (storedSubtotal / quantity) : 0;
      const unitPrice = parseFloat(
        item?.unit_price
        ?? (Number.isFinite(fallbackUnitFromSubtotal) ? fallbackUnitFromSubtotal : 0)
        ?? item?.menu_items?.price
        ?? 0
      ) || 0;
      const lineSubtotal = parseFloat(item?.subtotal || (unitPrice * quantity) || 0) || 0;
      const key = `${name}||${unitPrice.toFixed(2)}||${note}`;

      if (grouped.has(key)) {
        const existing = grouped.get(key);
        existing.quantity += quantity;
        existing.subtotal = parseFloat((existing.subtotal + lineSubtotal).toFixed(2));
      } else {
        grouped.set(key, {
          name,
          quantity,
          unitPrice,
          subtotal: parseFloat(lineSubtotal.toFixed(2)),
          specialInstructions: note
        });
      }
    });

    return Array.from(grouped.values());
  };

  const getLocalPrintServiceUrls = () => {
    const urls = [];
    const seen = new Set();
    const add = (value) => {
      if (!value) return;
      const normalized = String(value).replace(/\/$/, '');
      if (seen.has(normalized)) return;
      seen.add(normalized);
      urls.push(normalized);
    };

    const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
      ? String(window.location.origin).replace(/\/$/, '')
      : '';
    const port = (typeof window !== 'undefined' && window.location)
      ? String(window.location.port || '')
      : '';

    const preferred = process.env.REACT_APP_PRINT_AGENT_URL || 'http://localhost:9101';
    add(preferred);

    if (port === '9101') {
      add(origin);
    }

    add('http://localhost:9101');
    add('http://127.0.0.1:9101');

    return urls;
  };

  const postToLocalPrintService = async (printJob) => {
    const urls = getLocalPrintServiceUrls();
    let lastError = null;

    for (const baseUrl of urls) {
      try {
        const response = await fetch(`${baseUrl}/print`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(printJob)
        });

        if (!response.ok) {
          let message = `HTTP ${response.status}`;
          try {
            const data = await response.clone().json();
            message = data.error || data.message || message;
          } catch (e) {
            // ignore parse error
          }
          lastError = new Error(message);
          continue;
        }

        return { response, baseUrl };
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('No local print service is reachable');
  };

  const fetchCompletedOrders = async () => {
    try {
      setLoadingOrders(true);
      const response = await api.get('/api/bills/completed-orders');
      setCompletedOrders(response.data?.orders || []);
    } catch (error) {
      console.error('Failed to fetch completed orders:', error);
      setCompletedOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchBills = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/bills');
      const fetchedBills = response.data?.bills || [];
      setBills(fetchedBills);
      setTotalBills(
        typeof response.data?.total === 'number'
          ? response.data.total
          : fetchedBills.length
      );
    } catch (error) {
      console.error('Failed to fetch bills:', error);
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItemsForEdit = async () => {
    try {
      const response = await api.get('/api/menu?availableOnly=true');
      setMenuItemsForEdit(response.data?.items || []);
    } catch (error) {
      console.error('Failed to fetch menu items for bill edit:', error);
      setMenuItemsForEdit([]);
    }
  };

  const generateBill = async (orderId) => {
    if (generatingOrderId === orderId) {
      return;
    }

    setGeneratingOrderId(orderId);
    try {
      // First create the bill
      const createResponse = await api.post(`/api/bills/generate/${orderId}`, {});
      
      // Get the bill ID from response or fetch it
      let billId = createResponse.data?.billId || createResponse.data?.bill?.id;
      
      if (!billId) {
        // If bill ID not in response, fetch the bill by order ID
        const billsResponse = await api.get('/api/bills');
        const bill = billsResponse.data?.bills?.find(b => b.order_id === orderId);
        if (bill) {
          billId = bill.id;
        }
      }

      if (billId) {
        await printThermalBill(billId, billPrintCopies);
      } else {
        await alert('Bill generated but could not fetch bill ID for printing.', 'Print Warning', 'warning');
      }

      // Refresh data after generating bill
      setTimeout(() => {
        fetchCompletedOrders();
        fetchBills();
      }, 1000);
    } catch (error) {
      console.error('Failed to generate bill:', error);
      await alert('Failed to generate bill', 'Error', 'error');
    } finally {
      setGeneratingOrderId(null);
    }
  };

  const regenerateWithoutServiceCharge = async (bill) => {
    if (!bill?.id) {
      await alert('Bill ID not found for this bill.', 'Error', 'error');
      return;
    }

    if (regeneratingBillId === bill.id) {
      return;
    }

    setRegeneratingBillId(bill.id);
    try {
      const billId = bill.id;

      const response = await api.post(`/api/bills/${billId}/reprint`, {
        includeServiceCharge: false,
        paymentMethod: bill.payment_method || 'cash',
        cashierName: bill.cashier_name || null,
        discountAmount: bill.discount_amount || 0,
        returnPrintJob: true
      });

      const printJob = response.data?.printJob;
      const updatedBill = response.data?.bill;
      const updatedBillId = updatedBill?.id || billId;

      if (printJob) {
        await printThermalBillFromJob(printJob, updatedBillId);
      } else if (updatedBillId) {
        await printThermalBill(updatedBillId);
      } else {
        await alert('Bill updated but could not fetch bill ID for printing.', 'Print Warning', 'warning');
      }

      if (updatedBill) {
        setSelectedBill(updatedBill);
      }
      await fetchBills();
    } catch (error) {
      console.error('Failed to regenerate bill without service charge:', error);
      await alert('Failed to regenerate bill without service charge', 'Error', 'error');
    } finally {
      setRegeneratingBillId(null);
    }
  };

  const openDeleteBillModal = (bill) => {
    setDeletingBill(bill);
    setDeletePassword('');
    setShowDeleteBillModal(true);
  };

  const closeDeleteBillModal = () => {
    if (deletingBillId) return;
    setShowDeleteBillModal(false);
    setDeletingBill(null);
    setDeletePassword('');
  };

  const handleDeleteBill = async () => {
    if (!deletingBill?.id) {
      await alert('Bill not found', 'Error', 'error');
      return;
    }

    const trimmedPassword = String(deletePassword || '').trim();
    if (!trimmedPassword) {
      await alert('Please enter the bill delete password set in Owner Dashboard', 'Password Required', 'warning');
      return;
    }

    const finalConfirm = await confirm(
      `Delete bill ${deletingBill.bill_number || deletingBill.id}? This cannot be undone.`,
      'Confirm Bill Deletion',
      'warning'
    );
    if (!finalConfirm) return;

    setDeletingBillId(deletingBill.id);
    try {
      await api.delete(`/api/bills/${deletingBill.id}`, {
        data: { password: trimmedPassword }
      });

      await alert('Bill deleted successfully', 'Success', 'success');
      setShowDeleteBillModal(false);
      setDeletingBill(null);
      setDeletePassword('');

      if (selectedBill?.id === deletingBill.id) {
        setSelectedBill(null);
        setShowBillDetails(false);
      }

      await fetchBills();
      await fetchCompletedOrders();
    } catch (error) {
      console.error('Failed to delete bill:', error);
      await alert(error.response?.data?.error || 'Failed to delete bill', 'Delete Failed', 'error');
    } finally {
      setDeletingBillId(null);
    }
  };

  const printThermalBillFromJob = async (printJob, billId) => {
    try {
      const { response } = await postToLocalPrintService(printJob);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/octet-stream')) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bill-${billId}.escpos`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        await alert('⚠️ No printer found!\n\nESC/POS file downloaded instead.\n\nTo print:\n1. Connect a USB thermal printer\n2. Or configure a LAN printer\n3. Then try printing again', 'Printer Not Found', 'warning');
        return;
      }

      const result = await response.json();

      if (result.success) {
        await alert('✅ Bill sent to thermal printer successfully!', 'Success', 'success');
      } else {
        throw new Error(result.message || result.error || 'Print failed');
      }
    } catch (error) {
      console.error('Failed to print thermal bill:', error);

      if (error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.message.includes('Network request failed') ||
          error.message.includes('ERR_CONNECTION_REFUSED') ||
          error.name === 'TypeError') {
        await alert('❌ Local Print Agent is not running!\n\nPlease:\n1. Start the print agent: cd print-agent && npm start\n2. Make sure it\'s running on http://localhost:9101\n3. Try again', 'Print Agent Not Running', 'error');
      } else {
        await alert(`❌ Failed to print: ${error.message || 'Unknown error'}\n\nMake sure:\n- Local Print Agent is running\n- Printer is connected and configured`, 'Print Failed', 'error');
      }
    }
  };

  const printThermalBill = async (billId, copies = 1) => {
    if (printingBillId === billId) {
      return;
    }

    setPrintingBillId(billId);
    try {
      // Step 1: Get print job JSON from backend
      const printJobResponse = await api.get(`/api/bills/${billId}/print-job`);
      const printJob = printJobResponse.data;
      const copyCount = Number.isFinite(Number(copies)) && Number(copies) > 0 ? Math.min(5, Number(copies)) : 1;

      // Step 2: Get local print agent URL (default to localhost)
      // Step 3: Send print job to local print service
      for (let copyIndex = 0; copyIndex < copyCount; copyIndex++) {
        const { response } = await postToLocalPrintService(printJob);

        // Check if response is ok
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
          throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
        }

        // Check if response is a file download (when printer not found)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/octet-stream')) {
          // Printer not found - download the ESC/POS file
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `bill-${billId}.escpos`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);

          await alert('⚠️ No printer found!\n\nESC/POS file downloaded instead.\n\nTo print:\n1. Connect a USB thermal printer\n2. Or configure a LAN printer\n3. Then try printing again', 'Printer Not Found', 'warning');
          return;
        }

        // Try to parse as JSON (successful print)
        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || result.error || 'Print failed');
        }
      }

      await alert(`✅ Bill sent to thermal printer successfully (${copyCount} ${copyCount > 1 ? 'copies' : 'copy'})!`, 'Success', 'success');
    } catch (error) {
      console.error('Failed to print thermal bill:', error);
      
      // Check if it's a connection error (local agent not running)
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('NetworkError') || 
          error.message.includes('Network request failed') ||
          error.message.includes('ERR_CONNECTION_REFUSED') ||
          error.name === 'TypeError') {
        await alert('❌ Local print service is not running!\n\nPlease:\n1. Start print-agent: cd print-agent && npm start\n2. Ensure it is running on http://localhost:9101\n3. Try again', 'Print Agent Not Running', 'error');
      } else {
        await alert(`❌ Failed to print: ${error.message || 'Unknown error'}\n\nMake sure:\n- Print-agent is running on localhost:9101\n- Printer is connected and configured`, 'Print Failed', 'error');
      }
    } finally {
      setPrintingBillId(null);
    }
  };


  const viewBillDetails = async (bill) => {
    setSelectedBill(bill);
    setBillNoteDraft(String(bill?.bill_note || ''));
    setShowBillDetails(true);
  };

  const saveBillNote = async () => {
    if (!selectedBill?.id) {
      await alert('Bill not found', 'Error', 'error');
      return;
    }

    try {
      setSavingBillNote(true);
      const response = await api.patch(`/api/bills/${selectedBill.id}/note`, {
        note: billNoteDraft
      });

      const updatedBill = response.data?.bill;
      if (updatedBill) {
        setSelectedBill((prev) => prev ? { ...prev, bill_note: updatedBill.bill_note } : prev);
        setBills((prevBills) => prevBills.map((bill) => (
          bill.id === updatedBill.id ? { ...bill, bill_note: updatedBill.bill_note } : bill
        )));
      }

      await alert('Bill note saved successfully', 'Success', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to save bill note', 'Error', 'error');
    } finally {
      setSavingBillNote(false);
    }
  };

  const openBillPdfPreview = async (billId) => {
    try {
      const response = await api.get(`/api/bills/${billId}/pdf`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const objectUrl = window.URL.createObjectURL(blob);
      const previewWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer');

      if (!previewWindow) {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60000);
    } catch (error) {
      console.error('Failed to open bill PDF preview:', error);
      await alert(error.response?.data?.error || 'Failed to open bill PDF preview', 'PDF Preview Error', 'error');
    }
  };

  const openEditBillModal = async (bill) => {
    setEditingBill(bill);
    const existingItems = (bill?.orders?.order_items || []).map((item) => ({
      menuItemId: item.menu_item_id,
      quantity: item.quantity || 1,
      unitPrice: parseFloat(item.unit_price || 0) || 0,
      specialInstructions: item.special_instructions || ''
    }));

    setEditBillItems(existingItems.length > 0 ? existingItems : []);

    // Derive current discount % from stored bill values
    const existingDiscountAmount = parseFloat(bill?.discount_amount || 0);
    const subtotalAfterDiscount = parseFloat(bill?.subtotal || 0);
    const baseSubtotal = subtotalAfterDiscount + existingDiscountAmount;
    const initialDiscountPercent = baseSubtotal > 0
      ? (existingDiscountAmount / baseSubtotal) * 100
      : 0;
    setEditDiscountPercent(String(parseFloat(initialDiscountPercent.toFixed(2)) || 0));
    setEditDiscountType('percent');
    setEditDiscountFlatAmount(String(parseFloat(existingDiscountAmount.toFixed(2)) || '0'));
    setEditPaymentMethod((bill?.payment_method || 'cash').toLowerCase());

    if (menuItemsForEdit.length === 0) {
      await fetchMenuItemsForEdit();
    }

    setShowEditBillModal(true);
  };

  const closeEditBillModal = () => {
    setShowEditBillModal(false);
    setEditingBill(null);
    setEditBillItems([]);
    setEditDiscountPercent('0');
    setEditDiscountType('percent');
    setEditDiscountFlatAmount('0');
    setEditPaymentMethod('cash');
    setIsFullBillEditUnlocked(false);
    setBillEditUnlockPassword('');
    setVerifyingBillEditPassword(false);
  };

  const unlockFullBillEdit = async () => {
    if (!editingBill?.id) {
      await alert('Invalid bill selected', 'Error', 'error');
      return;
    }

    const trimmedPassword = String(billEditUnlockPassword || '').trim();
    if (!trimmedPassword) {
      await alert('Please enter bill delete password to unlock full edit', 'Password Required', 'warning');
      return;
    }

    try {
      setVerifyingBillEditPassword(true);
      await api.post(`/api/bills/${editingBill.id}/verify-edit-password`, {
        password: trimmedPassword
      });
      setIsFullBillEditUnlocked(true);
      await alert('Full bill edit unlocked', 'Unlocked', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Invalid bill delete password', 'Unlock Failed', 'error');
    } finally {
      setVerifyingBillEditPassword(false);
    }
  };

  const addEditBillItemRow = () => {
    setEditBillItems((prev) => ([
      ...prev,
      {
        menuItemId: '',
        quantity: 1,
        unitPrice: 0,
        specialInstructions: '',
        isNew: true
      }
    ]));
  };

  const updateEditBillItem = (index, field, value) => {
    setEditBillItems((prev) => prev.map((item, i) => {
      if (i !== index) return item;

      if (field === 'menuItemId') {
        const matchedMenu = menuItemsForEdit.find((menu) => menu.id === value);
        return {
          ...item,
          menuItemId: value,
          unitPrice: matchedMenu ? (parseFloat(matchedMenu.price || 0) || 0) : item.unitPrice
        };
      }

      return {
        ...item,
        [field]: value
      };
    }));
  };

  const removeEditBillItemRow = (index) => {
    setEditBillItems((prev) => prev.filter((_, i) => i !== index));
  };

  const saveBillEdit = async () => {
    if (!editingBill?.id) {
      await alert('Invalid bill selected for editing', 'Error', 'error');
      return;
    }

    const payloadItems = editBillItems
      .map((item) => ({
        menuItemId: item.menuItemId,
        quantity: parseInt(item.quantity, 10) || 0,
        unitPrice: parseFloat(item.unitPrice || 0) || 0,
        specialInstructions: item.specialInstructions || ''
      }))
      .filter((item) => item.menuItemId && item.quantity > 0);

    if (isFullBillEditUnlocked && payloadItems.length === 0) {
      await alert('Please keep at least one valid item in the bill', 'Validation Error', 'warning');
      return;
    }

    const editedItemsSubtotal = payloadItems.reduce((sum, item) => {
      return sum + ((parseFloat(item.unitPrice || 0) || 0) * item.quantity);
    }, 0);

    const parsedDiscountPercent = Math.min(100, Math.max(0, parseFloat(editDiscountPercent || 0) || 0));
    const parsedDiscountFlat = Math.min(editedItemsSubtotal, Math.max(0, parseFloat(editDiscountFlatAmount || 0) || 0));
    const calculatedDiscountAmount = editDiscountType === 'amount'
      ? parseFloat(parsedDiscountFlat.toFixed(2))
      : parseFloat(((editedItemsSubtotal * parsedDiscountPercent) / 100).toFixed(2));

    try {
      setSavingBillEdit(true);
      const response = await api.post(`/api/bills/${editingBill.id}/edit`, {
        items: isFullBillEditUnlocked ? payloadItems : undefined,
        discountAmount: isFullBillEditUnlocked ? calculatedDiscountAmount : undefined,
        paymentMethod: editPaymentMethod,
        returnPrintJob: false,
        editScope: isFullBillEditUnlocked ? 'full' : 'payment_only',
        editPassword: isFullBillEditUnlocked ? String(billEditUnlockPassword || '').trim() : undefined
      });

      const updatedBill = response.data?.bill;
      if (updatedBill && selectedBill?.id === updatedBill.id) {
        setSelectedBill(updatedBill);
      }

      await fetchBills();
      closeEditBillModal();
      await alert(
        isFullBillEditUnlocked
          ? 'Bill updated successfully. Edited tag has been applied for audit.'
          : 'Payment method updated successfully.',
        'Success',
        'success'
      );
    } catch (error) {
      console.error('Failed to edit bill:', error);
      await alert(error.response?.data?.error || 'Failed to edit bill', 'Error', 'error');
    } finally {
      setSavingBillEdit(false);
    }
  };

  // Convert to IST (Indian Standard Time) - Proper timezone handling (same as POS/KDS)
  const formatIST = (dateString) => {
    if (!dateString) return '';
    try {
      // Ensure timestamp is treated as UTC
      let date;
      if (typeof dateString === 'string') {
        // If timestamp doesn't have timezone info, append 'Z' to force UTC interpretation
        if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.match(/-\d{2}:\d{2}$/)) {
          // No timezone indicator - append 'Z' to force UTC interpretation
          date = new Date(dateString + 'Z');
        } else {
          // Has timezone info, parse normally
          date = new Date(dateString);
        }
      } else {
        date = dateString;
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return '';
      }
      
      // Format directly in IST timezone
      // The date object stores time in UTC internally, so toLocaleString with timeZone will convert correctly
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '';
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '';
    try {
      // Ensure timestamp is treated as UTC
      let date;
      if (typeof dateString === 'string') {
        // If timestamp doesn't have timezone info, append 'Z' to force UTC interpretation
        if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.match(/-\d{2}:\d{2}$/)) {
          // No timezone indicator - append 'Z' to force UTC interpretation
          date = new Date(dateString + 'Z');
        } else {
          // Has timezone info, parse normally
          date = new Date(dateString);
        }
      } else {
        date = dateString;
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return '';
      }
      
      // Return YYYY-MM-DD in IST timezone to match <input type="date"> value format
      const istDateParts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(date);

      const getPart = (type) => istDateParts.find((part) => part.type === type)?.value || '';
      return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '';
    }
  };

  const filteredBills = bills.filter(bill => {
    const matchesSearch = 
      bill.bill_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.orders?.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.orders?.tables?.table_number?.toString().includes(searchTerm);

    const billDateForFilter =
      bill.bill_date ||
      bill.generated_at ||
      bill.created_at ||
      bill.orders?.created_at;

    const formattedDate = formatDateOnly(billDateForFilter);
    const matchesPeriod = (() => {
      if (historyFilterMode === 'all') return true;
      if (!formattedDate) return false;
      if (historyFilterMode === 'date') {
        return formattedDate === selectedHistoryDate;
      }
      const [year = '', month = ''] = formattedDate.split('-');
      return year === selectedHistoryYear && month === selectedHistoryMonth;
    })();

    return matchesSearch && matchesPeriod;
  });

  const historyYearOptions = (() => {
    const years = new Set([String(currentYear)]);
    bills.forEach((bill) => {
      const billDateForFilter =
        bill.bill_date ||
        bill.generated_at ||
        bill.created_at ||
        bill.orders?.created_at;
      const formattedDate = formatDateOnly(billDateForFilter);
      if (!formattedDate) return;
      const [year = ''] = formattedDate.split('-');
      if (year) years.add(year);
    });

    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  })();

  const filteredCompletedOrders = completedOrders.filter(order => {
    const matchesSearch = 
      order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.tables?.table_number?.toString().includes(searchTerm);
    
    return matchesSearch;
  });

  const formatPaymentMethodLabel = (method) => {
    const normalized = String(method || '').trim();
    if (!normalized) return 'Other';
    return normalized
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const getBillDateObject = (bill) => {
    const sourceDate =
      bill?.bill_date ||
      bill?.generated_at ||
      bill?.created_at ||
      bill?.orders?.created_at;

    if (!sourceDate) return null;

    const hasTimezone = typeof sourceDate === 'string'
      && (sourceDate.includes('Z') || sourceDate.includes('+') || sourceDate.match(/-\d{2}:\d{2}$/));
    const parsed = new Date(typeof sourceDate === 'string' && !hasTimezone ? `${sourceDate}Z` : sourceDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getSelectedPeriodLabel = () => {
    if (historyFilterMode === 'date' && selectedHistoryDate) {
      return `${selectedHistoryDate} to ${selectedHistoryDate}`;
    }

    if (historyFilterMode === 'month') {
      const start = `${selectedHistoryYear}-${selectedHistoryMonth}-01`;
      const endDate = new Date(Number(selectedHistoryYear), Number(selectedHistoryMonth), 0);
      const end = `${selectedHistoryYear}-${selectedHistoryMonth}-${String(endDate.getDate()).padStart(2, '0')}`;
      return `${start} to ${end}`;
    }

    if (!filteredBills.length) {
      return 'All Time';
    }

    const timestamps = filteredBills
      .map((bill) => getBillDateObject(bill)?.getTime())
      .filter((time) => Number.isFinite(time));

    if (!timestamps.length) {
      return 'All Time';
    }

    const minDate = new Date(Math.min(...timestamps));
    const maxDate = new Date(Math.max(...timestamps));
    const toKey = (date) => {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(date);
      const getPart = (type) => parts.find((part) => part.type === type)?.value || '';
      return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
    };

    return `${toKey(minDate)} to ${toKey(maxDate)}`;
  };

  const buildExportSummary = () => {
    const configuredMethods = (Array.isArray(paymentMethodsForEdit) ? paymentMethodsForEdit : [])
      .map((method) => String(method || '').trim().toLowerCase())
      .filter(Boolean);

    const methodTotals = {};
    configuredMethods.forEach((method) => {
      methodTotals[method] = 0;
    });

    let periodSubtotal = 0;
    let periodTotal = 0;

    filteredBills.forEach((bill) => {
      const subtotal = parseFloat(bill?.subtotal || 0) || 0;
      const total = parseFloat(bill?.total_amount || 0) || 0;
      const paymentMethod = String(bill?.payment_method || 'cash').trim().toLowerCase() || 'cash';

      periodSubtotal += subtotal;
      periodTotal += total;
      methodTotals[paymentMethod] = (methodTotals[paymentMethod] || 0) + total;
    });

    const methodEntries = Object.entries(methodTotals)
      .filter(([, amount]) => amount > 0 || configuredMethods.length > 0)
      .map(([method, amount]) => ({
        method,
        label: formatPaymentMethodLabel(method),
        amount
      }));

    return {
      periodSubtotal,
      periodTotal,
      methodEntries
    };
  };

  const getExportRows = () => {
    return filteredBills.map((bill) => {
      const dateObj = getBillDateObject(bill);
      const datePart = dateObj ? dateObj.toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata' }) : '';
      const timePart = dateObj ? dateObj.toLocaleTimeString('en-GB', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }) : '';

      return {
        billNumber: bill.bill_number || '',
        orderNumber: bill.orders?.order_number || '',
        dateTime: formatIST(bill.created_at),
        datePart,
        timePart,
        table: bill.orders?.tables?.table_number ? `Table ${bill.orders.tables.table_number}` : '-',
        paymentMethod: formatPaymentMethodLabel(bill.payment_method || 'cash'),
        paymentStatus: bill.payment_status || '',
        subtotal: parseFloat(bill.subtotal || 0) || 0,
        discountAmount: parseFloat(bill.discount_amount || 0) || 0,
        serviceCharge: parseFloat(bill.service_charge || 0) || 0,
        taxAmount: parseFloat(bill.tax_amount || 0) || 0,
        totalAmount: parseFloat(bill.total_amount || 0) || 0,
        room: bill.room_number || bill.room_no || '-',
        guestName: bill.guest_name || bill.customer_name || bill.orders?.guest_name || '-'
      };
    });
  };

  const handleOpenExportOptions = async () => {
    if (!filteredBills.length) {
      await alert('No bill data available to export', 'Export', 'warning');
      return;
    }

    setShowExportFormatModal(true);
  };

  const handleDownloadBillsCsv = async () => {
    if (!filteredBills.length) {
      await alert('No bill data available to export', 'Export CSV', 'warning');
      return;
    }

    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value).replace(/"/g, '""');
      return /[",\n]/.test(str) ? `"${str}"` : str;
    };

    const formatNumber = (value) => parseFloat(value || 0).toFixed(2);
    const exportRows = getExportRows();
    const { periodSubtotal, periodTotal, methodEntries } = buildExportSummary();
    const periodLabel = getSelectedPeriodLabel();

    const headers = [
      'Bill Number',
      'Order Number',
      'Table',
      'Bill Date (IST)',
      'Payment Method',
      'Payment Status',
      'Subtotal',
      'Discount Amount',
      'Service Charge',
      'Tax Amount',
      'Total Amount'
    ];

    const rows = exportRows.map((row) => ([
      row.billNumber,
      row.orderNumber,
      row.table,
      row.dateTime,
      row.paymentMethod,
      row.paymentStatus,
      formatNumber(row.subtotal),
      formatNumber(row.discountAmount),
      formatNumber(row.serviceCharge),
      formatNumber(row.taxAmount),
      formatNumber(row.totalAmount)
    ]));

    rows.push([]);
    rows.push(['Report Period', periodLabel]);
    rows.push(['Period Subtotal', formatNumber(periodSubtotal)]);
    methodEntries.forEach((entry) => {
      rows.push([`Total ${entry.label}`, formatNumber(entry.amount)]);
    });
    rows.push(['Grand Total', formatNumber(periodTotal)]);

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateSuffix = new Date().toISOString().split('T')[0];

    link.href = url;
    link.download = `bills-export-${dateSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportFormatModal(false);
  };

  const handleDownloadBillsPdf = async () => {
    if (!filteredBills.length) {
      await alert('No bill data available to export', 'Export PDF', 'warning');
      return;
    }

    try {
      const exportRows = getExportRows();
      const { periodSubtotal, periodTotal, methodEntries } = buildExportSummary();
      const periodLabel = getSelectedPeriodLabel();
      const restaurantName =
        localStorage.getItem('restaurant_name') ||
        localStorage.getItem('restaurantName') ||
        'Restaurant';

      const nowIst = new Date().toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });


      const safe = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      const rowsHtml = exportRows.map((row) => `
        <tr>
          <td>${safe(row.billNumber)}</td>
          <td>${safe(row.datePart)}</td>
          <td>${safe(row.timePart)}</td>
          <td>${safe(row.table)}</td>
          <td class="right">${safe(row.totalAmount.toFixed(2))}</td>
          <td>${safe(row.paymentMethod)}</td>
          <td>${safe(row.room)}</td>
          <td>${safe(row.guestName)}</td>
        </tr>
      `).join('');

      const summaryHtml = methodEntries.map((entry) => `
        <tr>
          <td>${safe(entry.label)}</td>
          <td class="right">${safe(entry.amount.toFixed(2))}</td>
        </tr>
      `).join('');

      const html = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Bills Report</title>
            <style>
              body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
              .title { font-size: 18px; margin-bottom: 6px; }
              .sub { font-size: 13px; margin-bottom: 4px; }
              .meta { font-size: 12px; margin-bottom: 12px; }
              table { border-collapse: collapse; width: 100%; font-size: 11px; }
              th, td { border: 1px solid #777; padding: 6px; text-align: left; }
              th { background: #f4f4f4; }
              .right { text-align: right; }
              .summary { width: 260px; margin-top: 16px; }
              .print-time { font-size: 10px; margin-top: 10px; color: #333; }
              @media print { body { margin: 12mm; } }
            </style>
          </head>
          <body>
            <div class="title">${safe(restaurantName)}</div>
            <div class="sub">Bill No wise Restaurant Register All Bills</div>
            <div class="meta">Period from ${safe(periodLabel)} All Type</div>

            <table>
              <thead>
                <tr>
                  <th>Bill No</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Table</th>
                  <th>Total</th>
                  <th>Paid By</th>
                  <th>Room</th>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>

            <div style="margin-top: 10px; font-size: 12px;">Total: ${safe(periodTotal.toFixed(2))}</div>

            <table class="summary">
              <thead>
                <tr>
                  <th></th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                ${summaryHtml}
                <tr><td>Subtotal</td><td class="right">${safe(periodSubtotal.toFixed(2))}</td></tr>
                <tr><td>Total</td><td class="right">${safe(periodTotal.toFixed(2))}</td></tr>
              </tbody>
            </table>

            <div class="print-time">${safe(nowIst)}</div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank', 'noopener,noreferrer');
      if (!printWindow) {
        throw new Error('Popup blocked. Please allow popups to export PDF.');
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      setShowExportFormatModal(false);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      await alert('Failed to export PDF report', 'Export PDF', 'error');
    }
  };

  if (loading && loadingOrders) {
    return (
      <Layout title="Bills">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Bills Management">
      <div className="space-y-6">
        {/* Header Section */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Bills Management</h1>
          <p className="text-sm text-gray-600">Generate bills and view bill history</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-3 border-b-2 border-gray-200">
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-6 py-3 font-semibold text-base transition-all relative ${
              activeTab === 'completed'
                ? 'text-gray-900 border-b-2 border-gray-900'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Completed Orders ({completedOrders.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 font-semibold text-base transition-all relative ${
              activeTab === 'history'
                ? 'text-gray-900 border-b-2 border-gray-900'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Generated Bills ({totalBills})
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="text"
                placeholder="Search by order number, bill number, or table..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-sm"
              />
            </div>
            {activeTab === 'history' && (
              <>
                <button
                  onClick={handleOpenExportOptions}
                  className="px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors flex items-center gap-2"
                >
                  <FiDownload className="text-base" /> Download
                </button>
                <button
                  onClick={() => setHistoryFilterMode('all')}
                  className={`px-4 py-2.5 border rounded-lg text-sm font-medium transition-colors ${
                    historyFilterMode === 'all'
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  Show All
                </button>
                <div className="relative">
                  <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base" />
                  <input
                    type="date"
                    value={selectedHistoryDate}
                    onChange={(e) => {
                      setSelectedHistoryDate(e.target.value);
                      setHistoryFilterMode(e.target.value ? 'date' : 'all');
                    }}
                    className="pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-sm"
                  />
                </div>
                <div className="relative">
                  <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base" />
                  <select
                    value={selectedHistoryMonth}
                    onChange={(e) => {
                      setSelectedHistoryMonth(e.target.value);
                      setHistoryFilterMode('month');
                    }}
                    className="pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-sm"
                  >
                    <option value="01">January</option>
                    <option value="02">February</option>
                    <option value="03">March</option>
                    <option value="04">April</option>
                    <option value="05">May</option>
                    <option value="06">June</option>
                    <option value="07">July</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
                <div className="relative">
                  <select
                    value={selectedHistoryYear}
                    onChange={(e) => {
                      setSelectedHistoryYear(e.target.value);
                      setHistoryFilterMode('month');
                    }}
                    className="px-4 pr-8 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-sm"
                  >
                    {historyYearOptions.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                {historyFilterMode !== 'all' && (
                  <button
                    onClick={() => {
                      setHistoryFilterMode('all');
                      setSelectedHistoryDate('');
                    }}
                    className="px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors"
                  >
                    Clear Filter
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        {activeTab === 'history' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="text-xs text-gray-600 mb-1">Total Bills</div>
              <div className="text-xl font-bold text-gray-900">{filteredBills.length}</div>
            </div>
            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="text-xs text-gray-600 mb-1">Total Revenue</div>
              <div className="text-xl font-bold text-black">
                ₹{filteredBills.reduce((sum, bill) => sum + (parseFloat(bill.total_amount) || 0), 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="text-xs text-gray-600 mb-1">Paid Bills</div>
              <div className="text-xl font-bold text-black">
                {filteredBills.filter(b => b.payment_status === 'paid').length}
              </div>
            </div>
            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="text-xs text-gray-600 mb-1">Pending Bills</div>
              <div className="text-xl font-bold text-black">
                {filteredBills.filter(b => b.payment_status === 'pending').length}
              </div>
            </div>
          </div>
        )}

      {/* Completed Orders Tab */}
      {activeTab === 'completed' && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {loadingOrders ? (
            <div className="text-center py-12 text-gray-500">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="text-sm text-gray-500 mt-3">Loading orders...</p>
            </div>
          ) : filteredCompletedOrders.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FiFileText className="text-4xl text-gray-400" />
              </div>
              <p className="text-gray-700 text-xl font-semibold mb-2">No completed orders</p>
              <p className="text-gray-500 text-sm">Orders marked as complete in POS will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Order Number</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Table</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date (IST)</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredCompletedOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-900">{order.order_number}</td>
                      <td className="px-6 py-4 text-gray-700">
                        {order.tables?.table_number ? `Table ${order.tables.table_number}` : '-'}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {order.order_items?.length || 0} items
                      </td>
                      <td className="px-6 py-4 font-semibold text-gray-900">₹{parseFloat(order.total || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        {formatIST(order.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => generateBill(order.id)}
                          disabled={generatingOrderId === order.id}
                          className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm font-medium shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <FiFileText className="text-base" /> {generatingOrderId === order.id ? 'Generating...' : 'Generate Bill'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Generated Bills History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="text-sm text-gray-500 mt-3">Loading bills...</p>
            </div>
          ) : filteredBills.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FiFileText className="text-4xl text-gray-400" />
              </div>
              <p className="text-gray-700 text-xl font-semibold mb-2">No bills found</p>
              <p className="text-gray-500 text-sm">
                {searchTerm || historyFilterMode !== 'all' ? 'Try adjusting your filters' : 'Generated bills will appear here'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bill Number</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Order Number</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Table</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Subtotal</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tax</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment Method</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date (IST)</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredBills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        <div className="flex items-center gap-2">
                          <span>{bill.bill_number}</span>
                          {parseFloat(bill.discount_amount || 0) > 0 && (
                            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800">
                              Discount
                            </span>
                          )}
                          {bill.is_edited && (
                            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">
                              Edited
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{bill.orders?.order_number || '-'}</td>
                      <td className="px-6 py-4 text-gray-700">
                        {bill.orders?.tables?.table_number ? `Table ${bill.orders.tables.table_number}` : '-'}
                      </td>
                      <td className="px-6 py-4 text-gray-700">₹{parseFloat(bill.subtotal || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 text-gray-700">₹{parseFloat(bill.tax_amount || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 font-semibold text-gray-900">₹{parseFloat(bill.total_amount || 0).toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800 capitalize">
                          {bill.payment_method || 'cash'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                          bill.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                          bill.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {bill.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        {formatIST(bill.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-3">
                          {billPdfPreviewEnabled && (
                            <button
                              onClick={() => openBillPdfPreview(bill.id)}
                              className="text-black hover:text-gray-700 transition-colors p-1.5 hover:bg-gray-100 rounded"
                              title="Preview bill PDF"
                            >
                              <FiEye className="text-lg" />
                            </button>
                          )}
                          <button
                            onClick={() => viewBillDetails(bill)}
                            className="text-black hover:text-gray-700 transition-colors p-1.5 hover:bg-gray-100 rounded"
                            title="View details"
                          >
                            <FiFileText className="text-lg" />
                          </button>
                          <button
                            onClick={() => printThermalBill(bill.id)}
                            disabled={printingBillId === bill.id}
                            className="text-black hover:text-gray-700 transition-colors p-1.5 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Print to thermal printer"
                          >
                            <FiPrinter className="text-lg" />
                          </button>
                          <button
                            onClick={() => openEditBillModal(bill)}
                            className="text-black hover:text-gray-700 transition-colors p-1.5 hover:bg-gray-100 rounded"
                            title="Edit bill items"
                          >
                            <FiEdit2 className="text-lg" />
                          </button>
                          <button
                            onClick={() => openDeleteBillModal(bill)}
                            className="text-red-600 hover:text-red-700 transition-colors p-1.5 hover:bg-red-50 rounded"
                            title="Delete bill"
                          >
                            <FiTrash2 className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showExportFormatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Download Generated Bills</h3>
              <button
                onClick={() => setShowExportFormatModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded"
              >
                <FiX className="text-lg" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600">Choose export format for the selected bills period.</p>
              <button
                onClick={handleDownloadBillsCsv}
                className="w-full border border-gray-300 text-gray-800 py-2.5 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Download as CSV
              </button>
              <button
                onClick={handleDownloadBillsPdf}
                className="w-full bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 text-sm font-medium"
              >
                Download as PDF
              </button>
              <button
                onClick={() => setShowExportFormatModal(false)}
                className="w-full text-gray-600 py-2 rounded-lg hover:bg-gray-100 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill Details Modal */}
      {showBillDetails && selectedBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Bill Details</h2>
              <button
                onClick={() => setShowBillDetails(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Bill Number</p>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">{selectedBill.bill_number}</p>
                    {parseFloat(selectedBill.discount_amount || 0) > 0 && (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800">
                        Discount
                      </span>
                    )}
                    {selectedBill.is_edited && (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">
                        Edited
                      </span>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Order Number</p>
                  <p className="font-semibold text-gray-900">{selectedBill.orders?.order_number || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Table</p>
                  <p className="font-semibold text-gray-900">
                    {selectedBill.orders?.tables?.table_number ? `Table ${selectedBill.orders.tables.table_number}` : '-'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Date</p>
                  <p className="font-semibold text-gray-900">{formatIST(selectedBill.created_at)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Payment Method</p>
                  <p className="font-semibold text-gray-900 capitalize">{selectedBill.payment_method || 'cash'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Payment Status</p>
                  <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-semibold ${
                    selectedBill.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                    selectedBill.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {selectedBill.payment_status}
                  </span>
                </div>
              </div>

              {selectedBill.orders?.order_items && selectedBill.orders.order_items.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Items:</p>
                  <div className="space-y-2">
                    {getGroupedBillItems(selectedBill.orders.order_items).map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-gray-50 rounded-lg p-3">
                        <div>
                          <span className="text-sm text-gray-700">
                            {item.quantity}x {item.name || 'Item'}
                          </span>
                          {item.specialInstructions ? (
                            <p className="text-xs text-gray-500 italic mt-0.5">Note: {item.specialInstructions}</p>
                          ) : null}
                        </div>
                        <span className="text-sm font-semibold text-gray-900">₹{parseFloat(item.subtotal || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Bill Note (Optional)</p>
                  <textarea
                    value={billNoteDraft}
                    onChange={(e) => setBillNoteDraft(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    placeholder="Write a small note for this bill..."
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={saveBillNote}
                      disabled={savingBillNote}
                      className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {savingBillNote ? 'Saving Note...' : 'Save Note'}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="text-gray-900 font-semibold">₹{parseFloat(selectedBill.subtotal || 0).toFixed(2)}</span>
                </div>
                {selectedBill.discount_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Discount:</span>
                    <span className="text-gray-900 font-semibold">-₹{parseFloat(selectedBill.discount_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                {selectedBill.service_charge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service Charge:</span>
                    <span className="text-gray-900 font-semibold">₹{parseFloat(selectedBill.service_charge || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">CGST (2.5%):</span>
                  <span className="text-gray-900 font-semibold">₹{(parseFloat(selectedBill.tax_amount || 0) / 2).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">SGST (2.5%):</span>
                  <span className="text-gray-900 font-semibold">₹{(parseFloat(selectedBill.tax_amount || 0) / 2).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-300">
                  <span className="text-gray-900">Total:</span>
                  <span className="text-gray-900">₹{parseFloat(selectedBill.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => openEditBillModal(selectedBill)}
                  className="flex-1 bg-gray-800 text-white py-3 rounded-lg hover:bg-gray-900 flex items-center justify-center gap-2 font-semibold shadow-sm transition-all"
                >
                  <FiEdit2 className="text-base" /> Edit Bill
                </button>
                <button
                  onClick={() => printThermalBill(selectedBill.id)}
                  disabled={printingBillId === selectedBill.id}
                  className="flex-1 bg-gray-800 text-white py-3 rounded-lg hover:bg-gray-900 flex items-center justify-center gap-2 font-semibold shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <FiPrinter className="text-base" /> {printingBillId === selectedBill.id ? 'Printing...' : 'Print Thermal'}
                </button>
                {selectedBill.service_charge > 0 && (
                  <button
                    onClick={() => regenerateWithoutServiceCharge(selectedBill)}
                    disabled={regeneratingBillId === selectedBill.id}
                    className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-800 font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {regeneratingBillId === selectedBill.id ? 'Processing...' : 'Remove Service Charge & Print'}
                  </button>
                )}
                <button
                  onClick={() => openDeleteBillModal(selectedBill)}
                  className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 font-semibold transition-colors"
                >
                  Delete Bill
                </button>
                <button
                  onClick={() => setShowBillDetails(false)}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 font-semibold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Bill Modal */}
      {showEditBillModal && editingBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Edit Bill: {editingBill.bill_number}</h2>
              <button
                onClick={closeEditBillModal}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className={`rounded-lg border p-3 ${isFullBillEditUnlocked ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      {isFullBillEditUnlocked ? <FiUnlock className="text-green-600" /> : <FiLock className="text-amber-700" />}
                      {isFullBillEditUnlocked ? 'Full Bill Edit Unlocked' : 'Locked: Only Payment Method Editable'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {isFullBillEditUnlocked
                        ? 'Discount, items, quantity and price changes are enabled.'
                        : 'Use the owner-configured bill delete password to unlock full bill editing.'}
                    </p>
                  </div>
                </div>

                {!isFullBillEditUnlocked && (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <input
                      type="password"
                      value={billEditUnlockPassword}
                      onChange={(e) => setBillEditUnlockPassword(e.target.value)}
                      placeholder="Enter bill delete password"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      disabled={verifyingBillEditPassword || savingBillEdit}
                    />
                    <button
                      onClick={unlockFullBillEdit}
                      disabled={verifyingBillEditPassword || savingBillEdit}
                      className="px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium disabled:opacity-60"
                    >
                      {verifyingBillEditPassword ? 'Verifying...' : 'Unlock Full Edit'}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Update wrong/missing items. Saving will recalculate subtotal, service charge, tax and total.
                </p>
                <button
                  onClick={addEditBillItemRow}
                  disabled={!isFullBillEditUnlocked}
                  className="bg-gray-900 text-white px-3 py-2 rounded-lg hover:bg-gray-800 text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiPlus /> Add Item
                </button>
              </div>

              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Discount section */}
                  <div>
                    <label className="text-xs text-gray-600 font-medium">Discount</label>
                    {/* Type toggle */}
                    <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium mt-1">
                      <button
                        type="button"
                        onClick={() => setEditDiscountType('percent')}
                        disabled={!isFullBillEditUnlocked}
                        className={`flex-1 py-1.5 transition-colors ${
                          editDiscountType === 'percent' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        % Percentage
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditDiscountType('amount')}
                        disabled={!isFullBillEditUnlocked}
                        className={`flex-1 py-1.5 transition-colors ${
                          editDiscountType === 'amount' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        ₹ Flat Amount
                      </button>
                    </div>
                    {editDiscountType === 'percent' ? (
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={editDiscountPercent}
                        onChange={(e) => setEditDiscountPercent(e.target.value)}
                        className="w-full mt-1 px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        placeholder="e.g. 10"
                        disabled={!isFullBillEditUnlocked}
                      />
                    ) : (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editDiscountFlatAmount}
                        onChange={(e) => setEditDiscountFlatAmount(e.target.value)}
                        className="w-full mt-1 px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        placeholder="e.g. 50"
                        disabled={!isFullBillEditUnlocked}
                      />
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Discount amount: <span className="font-semibold text-gray-800">₹{(() => {
                        const menuPriceMap = new Map((menuItemsForEdit || []).map((m) => [m.id, parseFloat(m.price || 0)]));
                        const sub = editBillItems.reduce((sum, item) => {
                          const qty = Math.max(0, parseInt(item.quantity, 10) || 0);
                          const unitPrice = parseFloat(item.unitPrice || menuPriceMap.get(item.menuItemId) || 0) || 0;
                          return sum + (unitPrice * qty);
                        }, 0);
                        if (editDiscountType === 'amount') {
                          return Math.min(sub, Math.max(0, parseFloat(editDiscountFlatAmount || 0) || 0)).toFixed(2);
                        }
                        const pct = Math.min(100, Math.max(0, parseFloat(editDiscountPercent || 0) || 0));
                        return ((sub * pct) / 100).toFixed(2);
                      })()}</span>
                    </p>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label className="text-xs text-gray-600 font-medium">Payment Method</label>
                    <select
                      value={editPaymentMethod}
                      onChange={(e) => setEditPaymentMethod(e.target.value)}
                      className="w-full mt-1 px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    >
                      {Array.from(new Set([...(paymentMethodsForEdit || []), editPaymentMethod].filter(Boolean))).map((method) => (
                        <option key={method} value={method}>
                          {method.charAt(0).toUpperCase() + method.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {editBillItems.map((item, index) => (
                  <div key={`edit-item-${index}`} className={`grid grid-cols-12 gap-2 items-start border rounded-lg p-3 relative ${
                    item.isNew ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
                  }`}>
                    {item.isNew && (
                      <div className="absolute -top-2.5 left-3 px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded">
                        NEW ITEM
                      </div>
                    )}
                    <div className="col-span-12 md:col-span-5">
                      <label className="text-xs text-gray-600">Menu Item</label>
                      <select
                        value={item.menuItemId || ''}
                        onChange={(e) => updateEditBillItem(index, 'menuItemId', e.target.value)}
                        className="w-full mt-1 px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        disabled={!isFullBillEditUnlocked}
                      >
                        <option value="">Select Item</option>
                        {menuItemsForEdit.map((menuItem) => (
                          <option key={menuItem.id} value={menuItem.id}>
                            {menuItem.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="text-xs text-gray-600">Qty</label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateEditBillItem(index, 'quantity', e.target.value)}
                        className="w-full mt-1 px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        disabled={!isFullBillEditUnlocked}
                      />
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="text-xs text-gray-600">Price</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => updateEditBillItem(index, 'unitPrice', e.target.value)}
                        className="w-full mt-1 px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        disabled={!isFullBillEditUnlocked}
                      />
                    </div>
                    <div className="col-span-10 md:col-span-2">
                      <label className="text-xs text-gray-600">Special Note</label>
                      <input
                        type="text"
                        value={item.specialInstructions || ''}
                        onChange={(e) => updateEditBillItem(index, 'specialInstructions', e.target.value)}
                        className="w-full mt-1 px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        placeholder="Optional"
                        disabled={!isFullBillEditUnlocked}
                      />
                    </div>
                    <div className="col-span-2 md:col-span-1 flex items-end justify-end">
                      <button
                        onClick={() => removeEditBillItemRow(index)}
                        disabled={!isFullBillEditUnlocked}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Remove item"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-3 border-t border-gray-200">
                <button
                  onClick={saveBillEdit}
                  disabled={savingBillEdit}
                  className="flex-1 bg-gray-900 text-white py-3 rounded-lg hover:bg-gray-800 font-semibold transition-colors disabled:opacity-50"
                >
                  {savingBillEdit ? 'Saving...' : 'Save Bill Changes'}
                </button>
                <button
                  onClick={closeEditBillModal}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Bill Password Confirmation Modal */}
      {showDeleteBillModal && deletingBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Delete Bill</h2>
              <button
                onClick={closeDeleteBillModal}
                disabled={!!deletingBillId}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded disabled:opacity-50"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700">
                Enter the owner-configured bill delete password to delete bill <span className="font-semibold">{deletingBill.bill_number || deletingBill.id}</span>.
              </p>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter bill delete password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                disabled={!!deletingBillId}
              />
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteBill}
                  disabled={!!deletingBillId}
                  className="flex-1 bg-red-600 text-white py-2.5 rounded-lg hover:bg-red-700 font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deletingBillId ? 'Deleting...' : 'Delete Bill'}
                </button>
                <button
                  onClick={closeDeleteBillModal}
                  disabled={!!deletingBillId}
                  className="flex-1 bg-gray-200 text-gray-800 py-2.5 rounded-lg hover:bg-gray-300 font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
}

export default Bills;
